import React from 'react';
import { ArrowLeftIcon, ArrowRightIcon, CalendarCheckIcon, FileTextIcon, GaugeIcon, HourglassIcon, LightbulbIcon, PencilIcon, RefreshCwIcon, RepeatIcon, SearchXIcon, TriangleAlertIcon, UsersIcon } from 'lucide-react';
import { Avatar } from '../components/ui/Avatar';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { EmptyState } from '../components/ui/EmptyState';
import { TbdBadge } from '../components/ui/TbdBadge';
import { useHRStore } from '../hooks/useHRStore';
import type { Navigate, PolicyRule } from '../types/hr';
import type { IconComponent } from '../types/ui';
import { displayName } from '../utils/employees';
import { formatDate } from '../utils/format';
import { describePolicy, sortedTiers, tierForYears } from '../utils/policy';
import { serviceYears } from '../utils/timeOff';
import { AccrualScheduleCard } from '../components/timeoff/AccrualScheduleCard';
const SECTION_ICONS: Record<string, IconComponent> = {
  eligibility: HourglassIcon,
  accrual: RefreshCwIcon,
  caps: GaugeIcon,
  anniversary: CalendarCheckIcon,
  carryover: RepeatIcon
};
export function PolicyDetail({
  policyId,
  onNavigate
}: {
  policyId: string;
  onNavigate: Navigate;
}) {
  const {
    policies,
    employees
  } = useHRStore();
  const policy = policies.find(p => p.id === policyId);
  if (!policy) {
    return <EmptyState icon={SearchXIcon} title="Policy not found" action={<Button size="sm" onClick={() => onNavigate({
      page: 'timeoff',
      tab: 'policies'
    })} data-id="element-1026" data-mp="3mty3kpfmln2m">
            Back to policies
          </Button>} data-id="element-1025" data-mp="3qtf9tl2vmo8u" />;
  }
  const assigned = employees.filter(e => e.policyId === policy.id);
  return <div data-id="element-1027" data-mp="3d190ahu8bfid">
      <button type="button" onClick={() => onNavigate({
      page: 'timeoff',
      tab: 'policies'
    })} className="mb-4 inline-flex items-center gap-1.5 text-xs font-medium text-muted transition hover:text-navy" data-id="element-1028" data-mp="ectg61wjynq8">
        <ArrowLeftIcon className="h-3.5 w-3.5" data-id="element-1029" data-mp="sjn1rjos42qt" /> Time Off · Policies
      </button>

      <div className="mb-8 flex flex-col gap-4 md:flex-row md:items-end md:justify-between" data-id="element-1030" data-mp="xhy9novik36e">
        <div data-id="element-1031" data-mp="jqtjrehy6p99">
          <div className="flex flex-wrap items-center gap-2" data-id="element-1032" data-mp="jp8n9jc2ow8r">
            <Badge tone="green" dot data-id="element-1033" data-mp="nrvhfqw7ut94">
              Active
            </Badge>
            <Badge tone="navy" data-id="element-1034" data-mp="npshof59hlr7">{policy.type}-based</Badge>
            {policy.hasTbd && <TbdBadge data-id="element-1035" data-mp="9qj3fl0m91pv" />}
          </div>
          <h1 className="mt-3 text-2xl font-semibold tracking-tight text-navy md:text-[28px]" data-id="element-1036" data-mp="er73sxaqiuwr">{policy.name}</h1>
          <p className="mt-1.5 text-sm text-muted" data-id="element-1037" data-mp="rpbw92571j6">Read-only summary · last updated {formatDate(policy.updated)} by Jordan Avery</p>
        </div>
        <Button variant="primary" icon={PencilIcon} onClick={() => onNavigate({
        page: 'policy-edit',
        policyId: policy.id
      })} data-id="element-1038" data-mp="4tj8o697vle2">
          Edit policy
        </Button>
      </div>

      {policy.hasTbd && <div className="mb-6 flex items-start gap-3 rounded-2xl bg-amber-50 px-5 py-4 ring-1 ring-inset ring-amber-200" data-id="element-1039" data-mp="1z6ikdoah7vg2">
          <TriangleAlertIcon className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" data-id="element-1040" data-mp="3jmqd4vqwpqci" />
          <div data-id="element-1041" data-mp="3fpc71cyivyoq">
            <p className="text-sm font-semibold text-amber-800" data-id="element-1042" data-mp="3flob675lv06u">Some values are still to be decided</p>
            <p className="mt-0.5 text-sm text-amber-700" data-id="element-1043" data-mp="3tgqrzpbzq9yf">
              Fields marked <span className="font-semibold" data-id="element-1044" data-mp="3ommf6pg1n7pf">TBD</span> are placeholders. Employees stay enrolled; accruals post once values are set via “Edit policy”.
            </p>
          </div>
        </div>}

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-[minmax(0,1fr)_340px]" data-id="element-1045" data-mp="28bhywu85jzy">
        <div className="space-y-6" data-id="element-1046" data-mp="g72vah19j1n1">
          <Card title="Overview" icon={FileTextIcon} data-id="element-1047" data-mp="g601o1ck2l3f">
            <p className="text-sm leading-relaxed text-slate-700" data-id="element-1048" data-mp="bakqxi9uh5b8">{policy.overview}</p>
            <p className="mt-4 rounded-xl bg-canvas px-4 py-3 text-sm text-slate-600" data-id="element-1049" data-mp="2hyxs5qiv11l7">
              <span className="font-semibold text-navy" data-id="element-1050" data-mp="2d1610yq1hji2">In plain English: </span>
              {describePolicy(policy.form).sentence}
            </p>
          </Card>

          {policy.form.schedule && policy.form.schedule.length > 0 && <AccrualScheduleCard schedule={policy.form.schedule} tierCounts={sortedTiers(policy.form.schedule).map(tier => assigned.filter(e => tierForYears(policy.form.schedule ?? [], serviceYears(e))?.id === tier.id).length)} data-id="element-1051" data-mp="2jfbkkk6zwapq" />}

          {policy.sections.map(section => <Card key={section.id} title={section.title} icon={SECTION_ICONS[section.id] ?? FileTextIcon} data-id="element-1052" data-mp="20pn4tybpt0uy">
              <dl className="divide-y divide-slate-100" data-id="element-1053" data-mp="25oggdl7fyhsh">
                {section.rules.map(rule => <RuleRow key={rule.label} rule={rule} data-id="element-1054" data-mp="2agzvwnpovmzx" />)}
              </dl>

              {section.id === 'anniversary' && policy.example && <div className="mt-5 rounded-2xl bg-flame/[0.06] p-5 ring-1 ring-inset ring-flame/20" data-id="element-1055" data-mp="1hznel8451fpy">
                  <p className="flex items-center gap-2 text-sm font-semibold text-navy" data-id="element-1056" data-mp="1agxrf7sberkq">
                    <LightbulbIcon className="h-4 w-4 text-flame" data-id="element-1057" data-mp="15hku1t3ti23w" /> Worked example
                  </p>
                  <div className="mt-4 flex flex-wrap items-center gap-3" data-id="element-1058" data-mp="10hp0knsqxt2l">
                    <span className="rounded-xl bg-white px-4 py-2.5 text-sm font-semibold text-navy shadow-sm ring-1 ring-slate-200" data-id="element-1059" data-mp="10k1gywiuvvdj">{policy.example.before}</span>
                    <ArrowRightIcon className="h-4 w-4 text-flame" aria-hidden="true" data-id="element-1060" data-mp="rj3a86h199r9" />
                    <span className="rounded-xl bg-flame px-4 py-2.5 text-sm font-semibold text-white shadow-sm shadow-flame/30" data-id="element-1061" data-mp="mq1qnl5vsm1c">{policy.example.added}</span>
                    <ArrowRightIcon className="h-4 w-4 text-flame" aria-hidden="true" data-id="element-1062" data-mp="csw0qg90712p" />
                    <span className="rounded-xl bg-navy px-4 py-2.5 text-sm font-semibold text-white" data-id="element-1063" data-mp="cqt3bb5iu5ny">{policy.example.after}</span>
                  </div>
                  <p className="mt-4 text-sm leading-relaxed text-slate-600" data-id="element-1064" data-mp="3t8w7lkz9f5f">{policy.example.caption}</p>
                </div>}
            </Card>)}
        </div>

        <div className="space-y-6 xl:sticky xl:top-24 xl:self-start" data-id="element-1065" data-mp="3uce2dypc8vxx">
          <Card title="At a glance" data-id="element-1066" data-mp="3gbti0y3rys2b">
            <dl className="space-y-3" data-id="element-1067" data-mp="3gcv6q5dprd2i">
              {policy.highlights.map(h => <div key={h.label} className="flex items-center justify-between rounded-xl bg-canvas px-4 py-3" data-id="element-1068" data-mp="3lbo9p3vdjlof">
                  <dt className="text-sm text-muted" data-id="element-1069" data-mp="cw4ieae9kdc0">{h.label}</dt>
                  <dd className="text-sm font-semibold text-navy" data-id="element-1070" data-mp="gwy8naf6i482">{h.tbd ? <TbdBadge data-id="element-1071" data-mp="gyj3701htn5x" /> : h.value}</dd>
                </div>)}
            </dl>
          </Card>

          <Card title="Assigned employees" subtitle={`${assigned.length} people`} icon={UsersIcon} bodyClassName="px-3 pb-3" data-id="element-1072" data-mp="3376wo7ovomw">
            {assigned.length === 0 ? <p className="px-3 pb-2 text-sm text-muted" data-id="element-1073" data-mp="vs4hs69kmj5m">No employees assigned yet.</p> : <ul className="max-h-[420px] space-y-0.5 overflow-y-auto" data-id="element-1074" data-mp="10lqhyyyjsukj">
                {assigned.map(e => <li key={e.id} data-id="element-1075" data-mp="19x4t7yrz6vtg">
                    <button type="button" onClick={() => onNavigate({
                page: 'profile',
                employeeId: e.id,
                tab: 'timeoff'
              })} className="flex w-full items-center gap-3 rounded-xl px-3 py-2 text-left transition hover:bg-canvas/70" data-id="element-1076" data-mp="15y5r0lxmpd5p">
                      <Avatar name={displayName(e)} seed={e.id} size="sm" data-id="element-1077" data-mp="r1enc0gx9faa" />
                      <span className="min-w-0" data-id="element-1078" data-mp="mz9vn7a1asqa">
                        <span className="block truncate text-sm font-medium text-navy" data-id="element-1079" data-mp="n0kyn4vnnwgc">{displayName(e)}</span>
                        <span className="block truncate text-xs text-muted" data-id="element-1080" data-mp="10wgawrvju9ar">{e.department}</span>
                      </span>
                    </button>
                  </li>)}
              </ul>}
          </Card>
        </div>
      </div>
    </div>;
}
function RuleRow({
  rule
}: {
  rule: PolicyRule;
}) {
  return <div className="grid grid-cols-1 gap-1 py-3.5 first:pt-0 last:pb-0 sm:grid-cols-[220px_minmax(0,1fr)] sm:gap-6" data-id="element-1081" data-mp="5pz75br5klyg">
      <dt className="text-sm text-muted" data-id="element-1082" data-mp="9s2ky71ao2mw">{rule.label}</dt>
      <dd data-id="element-1083" data-mp="eq46qzq3ug22">
        {rule.tbd ? <span className="inline-flex flex-wrap items-center gap-2" data-id="element-1084" data-mp="ep2tqppj3jhp">
            <TbdBadge data-id="element-1085" data-mp="jgkjehm3pxnd" />
            <span className="text-xs text-mutedlight" data-id="element-1086" data-mp="jh44g771t25m">Placeholder — pending leadership decision</span>
          </span> : <span className="text-sm font-medium text-navy" data-id="element-1087" data-mp="ofnog22303vc">{rule.value}</span>}
        {rule.note && <p className="mt-1 text-xs text-muted" data-id="element-1088" data-mp="td72p1ys4gna">{rule.note}</p>}
      </dd>
    </div>;
}